import React from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// `medium` is the page-level placeholder — it owns the whole content region, so
// it can afford the vertical room. `small` is for a placeholder sitting inside a
// populated page (a section on a tab beside other content), where the full-size
// well pushes everything below it off the fold; it's about half the height.
const SIZES = {
    medium: {
        py: {xs: 5, sm: 7},
        icon: 72,
        iconFontSize: 34,
        iconSpacing: 2.5,
        titleVariant: 'h6',
        descriptionSpacing: 1,
        descriptionMaxWidth: 440,
        actionSpacing: 3
    },
    small: {
        py: {xs: 2.5, sm: 2.5},
        icon: 40,
        iconFontSize: 20,
        iconSpacing: 1.25,
        titleVariant: 'subtitle1',
        descriptionSpacing: 0.5,
        descriptionMaxWidth: 380,
        actionSpacing: 1.75
    }
};

/**
 * Centered empty-state placeholder on a soft dashed card — for sections with no
 * data yet (no tanks, no records, etc.).
 *
 * @param {React.ReactNode} icon   Outline icon, shown in a circular chip. Colour
 *                                 is inherited, so pass an unstyled icon.
 * @param {React.ReactNode} title  Short headline (e.g. "No fuel tanks yet").
 * @param {React.ReactNode} description  One or two lines of guidance.
 * @param {React.ReactNode} action Footer slot — a primary button, or a muted
 *                                 hint when the user can't act.
 * @param {'medium'|'small'} size  `small` roughly halves the height, for a
 *                                 placeholder nested in an otherwise full page.
 */
export default function EmptyState({icon, title, description, action, size = 'medium', sx}) {
    const tokens = SIZES[size] || SIZES.medium;

    return (
        <Card
            variant="outlined"
            sx={{
                borderStyle: 'dashed',
                borderColor: 'divider',
                // The theme's outlined-Card variant forces a white background;
                // override to a warm well that sits clearly below the page bg.
                bgcolor: 'grey.100',
                boxShadow: 'none',
                ...sx
            }}
        >
            <Stack
                sx={{alignItems: 'center', textAlign: 'center', px: 3, py: tokens.py}}
            >
                {icon && (
                    <Box
                        sx={{
                            width: tokens.icon,
                            height: tokens.icon,
                            mb: tokens.iconSpacing,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.disabled',
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            '& > svg': {fontSize: tokens.iconFontSize}
                        }}
                    >
                        {icon}
                    </Box>
                )}

                <Typography variant={tokens.titleVariant} sx={{fontWeight: 600, lineHeight: 1.3}}>
                    {title}
                </Typography>

                {description && (
                    <Typography variant="body2" sx={{color: 'text.secondary', maxWidth: tokens.descriptionMaxWidth, mt: tokens.descriptionSpacing, lineHeight: 1.6}}>
                        {description}
                    </Typography>
                )}

                {action && <Box sx={{mt: tokens.actionSpacing}}>{action}</Box>}
            </Stack>
        </Card>
    );
}
